import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBold,
    faItalic,
    faStrikethrough,
    faListUl,
    faListOl,
    faHeading,
    faQuoteLeft,
    faLink,
    faLinkSlash,
    faRotateLeft,
    faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import "./RichTextEditor.css";

const ToolbarButton = ({ onClick, active, disabled, icon, title }) => (
    <button
        type="button"
        className={`rte__btn ${active ? "rte__btn--active" : ""}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        disabled={disabled}
        title={title}
    >
        <FontAwesomeIcon icon={icon} />
    </button>
);

const RichTextEditor = ({ value, onChange, placeholder }) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
            }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                defaultProtocol: "https",
            }),
        ],
        content: value || "",
        onUpdate: ({ editor: e }) => {
            const html = e.getHTML();
            onChange(e.isEmpty ? "" : html);
        },
    });

    // Keep editor in sync when the loaded record changes externally
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const incoming = value || "";
        if (incoming !== current && incoming !== (editor.isEmpty ? "" : current)) {
            editor.commands.setContent(incoming, { emitUpdate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, value]);

    if (!editor) return null;

    const setLink = () => {
        const previous = editor.getAttributes("link").href;
        const url = window.prompt("Link URL", previous || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().unsetLink().run();
            return;
        }
        editor.chain().focus().setLink({ href: url }).run();
    };

    return (
        <div className="rte">
            <div className="rte__toolbar">
                <ToolbarButton
                    icon={faHeading}
                    title="Heading"
                    active={editor.isActive("heading", { level: 3 })}
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 3 }).run()
                    }
                />
                <ToolbarButton
                    icon={faBold}
                    title="Bold"
                    active={editor.isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                />
                <ToolbarButton
                    icon={faItalic}
                    title="Italic"
                    active={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                />
                <ToolbarButton
                    icon={faStrikethrough}
                    title="Strikethrough"
                    active={editor.isActive("strike")}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                />
                <span className="rte__divider" />
                <ToolbarButton
                    icon={faListUl}
                    title="Bullet list"
                    active={editor.isActive("bulletList")}
                    onClick={() =>
                        editor.chain().focus().toggleBulletList().run()
                    }
                />
                <ToolbarButton
                    icon={faListOl}
                    title="Numbered list"
                    active={editor.isActive("orderedList")}
                    onClick={() =>
                        editor.chain().focus().toggleOrderedList().run()
                    }
                />
                <ToolbarButton
                    icon={faQuoteLeft}
                    title="Quote"
                    active={editor.isActive("blockquote")}
                    onClick={() =>
                        editor.chain().focus().toggleBlockquote().run()
                    }
                />
                <span className="rte__divider" />
                <ToolbarButton
                    icon={faLink}
                    title="Add link"
                    active={editor.isActive("link")}
                    onClick={setLink}
                />
                <ToolbarButton
                    icon={faLinkSlash}
                    title="Remove link"
                    disabled={!editor.isActive("link")}
                    onClick={() => editor.chain().focus().unsetLink().run()}
                />
                <span className="rte__divider" />
                <ToolbarButton
                    icon={faRotateLeft}
                    title="Undo"
                    disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}
                />
                <ToolbarButton
                    icon={faRotateRight}
                    title="Redo"
                    disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}
                />
            </div>
            <EditorContent
                className="rte__content"
                editor={editor}
                data-placeholder={placeholder}
            />
        </div>
    );
};

export default RichTextEditor;
